import React from "react";

import {
  CONNECTION_HEALTH_FLAP_THRESHOLD,
  CONNECTION_HEALTH_LAYER_DOWN_MS,
  CONNECTION_HEALTH_SESSION_WARMUP_MS,
  CONNECTION_HEALTH_TICK_MS,
  CONNECTION_HEALTH_UNSTABLE_CLEAR_MS,
} from "~/context/call/connectionHealth/constants";
import {
  countFlapsInWindow,
  pruneFlapTimestamps,
} from "~/context/call/connectionHealth/flapTracker";
import { logConnectionFlap } from "~/context/call/connectionHealth/logConnectionFlap";
import { resolveConnectionHealth } from "~/context/call/connectionHealth/resolveConnectionHealth";
import type {
  ConnectionFlapEventType,
  ConnectionHealthRecorder,
  ConnectionHealthSnapshot,
} from "~/context/call/connectionHealth/types";
import type { SocketStatus } from "~/context/call/callState";
import type { SipRegistrationStatus } from "~/services/calling/types";

export const CONNECTION_HEALTH_INITIAL_SNAPSHOT: ConnectionHealthSnapshot = {
  status: "offline",
  canTakeCalls: false,
  canGoAvailable: false,
  isNetworkOnline: true,
  layers: {
    dashboardSocket: "down",
    sipWebphone: "down",
    network: "healthy",
  },
  totalFlapsLast60s: 0,
  socketFlapsLast60s: 0,
  sipFlapsLast60s: 0,
  lastDropAtMs: null,
  lastDropSecondsAgo: null,
  unstableActive: false,
};

type FlapDispatch = (eventType: ConnectionFlapEventType) => void;

export const createConnectionHealthRecorder = (
  dispatchFlap: FlapDispatch
): ConnectionHealthRecorder => ({
  recordSocketDisconnect: () => dispatchFlap("socket_disconnect"),
  recordSocketReconnectAttempt: () => dispatchFlap("socket_reconnect_attempt"),
  recordSocketConnect: () => dispatchFlap("socket_connect"),
  recordSipUnregister: () => dispatchFlap("sip_unregister"),
  recordSipRegister: () => dispatchFlap("sip_register"),
  recordSipTransportDisconnect: () => dispatchFlap("sip_transport_disconnect"),
  recordSipTransportConnect: () => dispatchFlap("sip_transport_connect"),
});

export type UseConnectionHealthParams = {
  enabled: boolean;
  socketStatus: SocketStatus;
  sipStatus: SipRegistrationStatus;
  socketRef: React.RefObject<{ isReconnecting: () => boolean } | null>;
  extension?: string | null;
  recorderRef: React.MutableRefObject<ConnectionHealthRecorder>;
  canTakeCallsRef: React.MutableRefObject<boolean>;
  /** Dev/QA: record flaps even when monitoring is disabled (window.__aidcallConnectionHealth). */
  recordFlapRef?: React.MutableRefObject<(eventType: ConnectionFlapEventType) => void>;
};

export const useConnectionHealth = ({
  enabled,
  socketStatus,
  sipStatus,
  socketRef,
  extension,
  recorderRef,
  canTakeCallsRef,
  recordFlapRef,
}: UseConnectionHealthParams) => {
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  const [isNetworkOnline, setIsNetworkOnline] = React.useState(
    () => typeof navigator !== "undefined" && navigator.onLine
  );
  const [socketReconnecting, setSocketReconnecting] = React.useState(false);
  const [socketFlapTimestamps, setSocketFlapTimestamps] = React.useState<
    number[]
  >([]);
  const [sipFlapTimestamps, setSipFlapTimestamps] = React.useState<number[]>(
    []
  );
  const [layerDownSinceMs, setLayerDownSinceMs] = React.useState<{
    socket: number | null;
    sip: number | null;
  }>({ socket: null, sip: null });
  const [unstableActive, setUnstableActive] = React.useState(false);
  const [lastDisruptionAtMs, setLastDisruptionAtMs] = React.useState<
    number | null
  >(null);

  const sessionStartedAtMsRef = React.useRef<number | null>(null);
  const hasBeenFullyHealthyRef = React.useRef(false);
  const socketWasEverHealthyRef = React.useRef(false);
  const sipWasEverHealthyRef = React.useRef(false);

  const resetSessionHealthState = React.useCallback(() => {
    sessionStartedAtMsRef.current = null;
    hasBeenFullyHealthyRef.current = false;
    socketWasEverHealthyRef.current = false;
    sipWasEverHealthyRef.current = false;
    setSocketFlapTimestamps([]);
    setSipFlapTimestamps([]);
    setUnstableActive(false);
    setLastDisruptionAtMs(null);
    setLayerDownSinceMs({ socket: null, sip: null });
  }, []);

  const isUnstableMonitoringActive = React.useCallback((atMs: number) => {
    if (hasBeenFullyHealthyRef.current) return true;
    const startedAt = sessionStartedAtMsRef.current;
    if (startedAt == null) return false;
    return atMs - startedAt >= CONNECTION_HEALTH_SESSION_WARMUP_MS;
  }, []);

  const markDisruption = React.useCallback((atMs: number) => {
    if (!isUnstableMonitoringActive(atMs)) return;
    setLastDisruptionAtMs(atMs);
    setUnstableActive(true);
  }, [isUnstableMonitoringActive]);

  const recordFlapEvent = React.useCallback(
    (eventType: ConnectionFlapEventType) => {
      const atMs = Date.now();
      logConnectionFlap({
        type: eventType,
        socketStatus,
        sipStatus,
        extension,
      });

      if (
        eventType === "socket_disconnect" ||
        eventType === "socket_reconnect_attempt"
      ) {
        setSocketFlapTimestamps((prev) =>
          pruneFlapTimestamps([...prev, atMs], atMs)
        );
        markDisruption(atMs);
      }

      if (
        eventType === "sip_unregister" ||
        eventType === "sip_transport_disconnect"
      ) {
        setSipFlapTimestamps((prev) =>
          pruneFlapTimestamps([...prev, atMs], atMs)
        );
        markDisruption(atMs);
      }
    },
    [extension, markDisruption, sipStatus, socketStatus]
  );

  const dispatchFlap = React.useCallback(
    (eventType: ConnectionFlapEventType) => {
      if (!enabled) return;
      if (!isUnstableMonitoringActive(Date.now())) return;
      recordFlapEvent(eventType);
    },
    [enabled, isUnstableMonitoringActive, recordFlapEvent]
  );

  React.useEffect(() => {
    if (!enabled) {
      resetSessionHealthState();
      return;
    }

    sessionStartedAtMsRef.current = Date.now();
    hasBeenFullyHealthyRef.current = false;
    socketWasEverHealthyRef.current = false;
    sipWasEverHealthyRef.current = false;
    setSocketFlapTimestamps([]);
    setSipFlapTimestamps([]);
    setUnstableActive(false);
    setLastDisruptionAtMs(null);
    setLayerDownSinceMs({ socket: null, sip: null });
  }, [enabled, resetSessionHealthState]);

  React.useEffect(() => {
    if (!enabled) return;

    if (socketStatus === "connected") {
      socketWasEverHealthyRef.current = true;
    }

    if (sipStatus === "registered") {
      sipWasEverHealthyRef.current = true;
    }

    if (socketStatus !== "connected" || sipStatus !== "registered") return;

    if (hasBeenFullyHealthyRef.current) return;

    hasBeenFullyHealthyRef.current = true;
    setUnstableActive(false);
    setLastDisruptionAtMs(null);
  }, [enabled, sipStatus, socketStatus]);

  React.useEffect(() => {
    if (!recordFlapRef) return;
    recordFlapRef.current = recordFlapEvent;
  }, [recordFlapEvent, recordFlapRef]);

  React.useEffect(() => {
    recorderRef.current = createConnectionHealthRecorder(dispatchFlap);
  }, [dispatchFlap, recorderRef]);

  React.useEffect(() => {
    if (!enabled) return;

    const readNetworkOnline = () =>
      typeof navigator !== "undefined" && navigator.onLine;

    const handleOnline = () => {
      setIsNetworkOnline(true);
      logConnectionFlap({
        type: "network_online",
        socketStatus,
        sipStatus,
        extension,
      });
    };

    const handleOffline = () => {
      setIsNetworkOnline(false);
      logConnectionFlap({
        type: "network_offline",
        socketStatus,
        sipStatus,
        extension,
      });
      markDisruption(Date.now());
    };

    setIsNetworkOnline(readNetworkOnline());
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [enabled, extension, markDisruption, sipStatus, socketStatus]);

  React.useEffect(() => {
    if (!enabled) return;

    const tick = window.setInterval(() => {
      setNowMs(Date.now());
      setSocketReconnecting(socketRef.current?.isReconnecting() ?? false);
    }, CONNECTION_HEALTH_TICK_MS);

    return () => window.clearInterval(tick);
  }, [enabled, socketRef]);

  React.useEffect(() => {
    if (!enabled) return;

    setLayerDownSinceMs((prev) => {
      const socketDown =
        socketStatus !== "connected" && socketStatus !== "connecting";
      const sipDown = sipStatus !== "registered" && sipStatus !== "connecting";

      const next = {
        socket: socketDown
          ? (prev.socket ?? Date.now())
          : null,
        sip: sipDown ? (prev.sip ?? Date.now()) : null,
      };

      const socketDownTooLong =
        socketWasEverHealthyRef.current &&
        next.socket != null &&
        Date.now() - next.socket >= CONNECTION_HEALTH_LAYER_DOWN_MS;
      const sipDownTooLong =
        sipWasEverHealthyRef.current &&
        next.sip != null &&
        Date.now() - next.sip >= CONNECTION_HEALTH_LAYER_DOWN_MS;

      if (
        (socketDownTooLong || sipDownTooLong) &&
        isUnstableMonitoringActive(Date.now())
      ) {
        markDisruption(Date.now());
      }

      if (prev.socket === next.socket && prev.sip === next.sip) return prev;
      return next;
    });
  }, [enabled, isUnstableMonitoringActive, markDisruption, sipStatus, socketStatus]);

  React.useEffect(() => {
    if (!enabled) return;

    if (!isUnstableMonitoringActive(nowMs)) return;

    const totalFlaps =
      countFlapsInWindow(socketFlapTimestamps, nowMs) +
      countFlapsInWindow(sipFlapTimestamps, nowMs);

    if (totalFlaps >= CONNECTION_HEALTH_FLAP_THRESHOLD) {
      setUnstableActive(true);
    }

    if (!unstableActive || lastDisruptionAtMs == null) return;

    if (nowMs - lastDisruptionAtMs >= CONNECTION_HEALTH_UNSTABLE_CLEAR_MS) {
      setUnstableActive(false);
      setLastDisruptionAtMs(null);
    }
  }, [
    enabled,
    isUnstableMonitoringActive,
    lastDisruptionAtMs,
    nowMs,
    sipFlapTimestamps,
    socketFlapTimestamps,
    unstableActive,
  ]);

  const snapshot = React.useMemo(() => {
    if (!enabled) return CONNECTION_HEALTH_INITIAL_SNAPSHOT;

    return resolveConnectionHealth({
      socketStatus,
      sipStatus,
      socketReconnecting,
      isNetworkOnline,
      socketFlapTimestamps,
      sipFlapTimestamps,
      layerDownSinceMs,
      unstableActive,
      nowMs,
    });
  }, [
    enabled,
    isNetworkOnline,
    layerDownSinceMs,
    nowMs,
    sipFlapTimestamps,
    sipStatus,
    socketFlapTimestamps,
    socketReconnecting,
    socketStatus,
    unstableActive,
  ]);

  React.useEffect(() => {
    canTakeCallsRef.current = snapshot.canTakeCalls;
  }, [canTakeCallsRef, snapshot.canTakeCalls]);

  return snapshot;
};
